/**
 * Virtual Try-On — shared asset (Shopify assets/tryon.js)
 *
 * Loaded once via {{ 'tryon.js' | asset_url | script_tag }} and shared
 * across every section that needs virtual try-on (the standalone promo/
 * mobile sections and the product-detail-page modal). Each section's own
 * Liquid template stays responsible for its own markup/CSS and simply
 * calls window.VirtualTryOn.init(config) with its OWN element IDs — this
 * file never assumes or imposes any particular class names/markup, so it
 * never influences the existing UI. Everything here is purely behavior;
 * every feature (remove, download, lightbox, photo editor, progress bar)
 * is opt-in via config and is a no-op if the corresponding element ID
 * isn't supplied.
 *
 * API reference: api.nairaa.shop/api/v1/tryon/ (see project reference
 * script for the original contract this was modeled on).
 *   POST  /api/v1/tryon/            -> { request_id }
 *   GET   /api/v1/tryon/{request_id} -> { status: 'completed'|'failed'|..., result_url }
 */
(function (window) {
  "use strict";

  var API_BASE = "https://api.nairaa.shop/api/v1/tryon/";
  var POLL_INTERVAL_MS = 3000;
  var MAX_POLLS = 40; // ~2 minutes at 3s/poll — bounded, unlike unbounded reference polling

  // Baseline for the fake progress bar. A real try-on takes ~10-15s, so the
  // curve is tuned to feel "done" right around 12.5s without ever claiming
  // 100% before the real API actually responds.
  var FAKE_PROGRESS_BASELINE_MS = 12500;
  var FAKE_PROGRESS_CAP = 92; // the fake animation never exceeds this on its own
  var FAKE_PROGRESS_STAGES = [
    { at: 0, label: "Uploading your photo…" },
    { at: 0.18, label: "Analyzing pose & lighting…" },
    { at: 0.45, label: "Fitting the garment…" },
    { at: 0.75, label: "Rendering your try-on…" }
  ];

  /**
   * @param {Object} config
   * Required:
   * @param {string} config.fileInputId       - hidden <input type="file">
   * @param {string} config.runButtonId       - "Try On" button that submits the request
   * @param {string} config.beforeSlotId      - container to render the uploaded photo into
   * @param {string} config.productId         - Shopify product id
   *
   * Optional — purely additive, no-op if the ID isn't in your markup:
   * @param {string} [config.uploadButtonId]  - button that opens the file picker
   * @param {string} [config.afterSlotId]     - container to render the try-on result into
   * @param {string} [config.statusId]        - element for status/progress text
   * @param {string} [config.removeButtonId]  - clears the uploaded photo and resets state
   * @param {string} [config.downloadButtonId]- downloads the try-on result image
   * @param {string} [config.loadingClassTargetId] - element to receive a "loading" class
   *                                            while a request is in flight (e.g. your
   *                                            existing upload box, for a shimmer/dim state
   *                                            you already style via your own CSS)
   * @param {string} [config.overlayId]       - full-screen lightbox container (must already
   *                                            exist in your markup with its own styling);
   *                                            clicking the before/after image opens it
   * @param {string} [config.overlayImageId]  - <img> inside the overlay that receives the src
   * @param {string} [config.overlayCloseId]  - close button inside the overlay
   * @param {string} [config.garmentUrl]      - static fallback garment image URL
   * @param {Function} [config.getGarmentUrl] - optional fn returning the garment URL at
   *                                            submit time (e.g. read the shopper's
   *                                            currently active gallery image instead
   *                                            of always using the default product image)
   * @param {Function} [config.onSuccess]     - optional fn(resultUrl) called on completion,
   *                                            e.g. to also swap a PDP's main gallery image
   * @param {Function} [config.onReset]       - optional fn() called when the remove button
   *                                            clears the current photo/result
   *
   * New, all optional/additive — omit any of them and that feature is a no-op:
   * @param {string} [config.editorOverlayId]     - full-screen editor modal wrapper
   * @param {string} [config.editorCanvasId]       - <canvas> the shopper edits on
   * @param {string} [config.editorZoomRangeId]    - range input, zoom
   * @param {string} [config.editorRotateLeftId]   - button, rotate -90°
   * @param {string} [config.editorRotateRightId]  - button, rotate +90°
   * @param {string} [config.editorResetId]        - button, reset pan/zoom/rotation
   * @param {string} [config.editorApplyId]        - button, confirm edit ("Use Photo")
   * @param {string} [config.editorCancelId]       - button, discard edit
   * @param {string} [config.progressTrackId]      - progress bar track element
   * @param {string} [config.progressFillId]       - progress bar fill element (width animated)
   * @param {string} [config.progressPercentId]    - element showing "NN%"
   * @param {string} [config.progressLabelId]      - element showing the stage label
   * @param {string} [config.progressWrapId]       - wrapper shown/hidden around the progress UI
   * @param {number} [config.fakeProgressDurationMs] - override FAKE_PROGRESS_BASELINE_MS
   * @returns {{ destroy: Function, stopPolling: Function }}
   */
  function init(config) {
    var uploadBtn = config.uploadButtonId ? document.getElementById(config.uploadButtonId) : null;
    var fileInput = document.getElementById(config.fileInputId);
    var runBtn = document.getElementById(config.runButtonId);
    var beforeSlot = document.getElementById(config.beforeSlotId);
    var afterSlot = config.afterSlotId ? document.getElementById(config.afterSlotId) : null;
    var statusEl = config.statusId ? document.getElementById(config.statusId) : null;
    var removeBtn = config.removeButtonId ? document.getElementById(config.removeButtonId) : null;
    var downloadBtn = config.downloadButtonId ? document.getElementById(config.downloadButtonId) : null;
    var loadingTarget = config.loadingClassTargetId ? document.getElementById(config.loadingClassTargetId) : null;
    var overlay = config.overlayId ? document.getElementById(config.overlayId) : null;
    var overlayImg = config.overlayImageId ? document.getElementById(config.overlayImageId) : null;
    var overlayClose = config.overlayCloseId ? document.getElementById(config.overlayCloseId) : null;

    // --- editor elements (all optional) ---
    var editorOverlay = config.editorOverlayId ? document.getElementById(config.editorOverlayId) : null;
    var editorCanvas = config.editorCanvasId ? document.getElementById(config.editorCanvasId) : null;
    var editorZoomRange = config.editorZoomRangeId ? document.getElementById(config.editorZoomRangeId) : null;
    var editorRotateLeft = config.editorRotateLeftId ? document.getElementById(config.editorRotateLeftId) : null;
    var editorRotateRight = config.editorRotateRightId ? document.getElementById(config.editorRotateRightId) : null;
    var editorResetBtn = config.editorResetId ? document.getElementById(config.editorResetId) : null;
    var editorApplyBtn = config.editorApplyId ? document.getElementById(config.editorApplyId) : null;
    var editorCancelBtn = config.editorCancelId ? document.getElementById(config.editorCancelId) : null;
    var editorCtx = editorCanvas && editorCanvas.getContext ? editorCanvas.getContext("2d") : null;
    var editorEnabled = !!(editorOverlay && editorCanvas && editorCtx);

    // --- progress elements (all optional) ---
    var progressWrap = config.progressWrapId ? document.getElementById(config.progressWrapId) : null;
    var progressTrack = config.progressTrackId ? document.getElementById(config.progressTrackId) : null;
    var progressFill = config.progressFillId ? document.getElementById(config.progressFillId) : null;
    var progressPercentEl = config.progressPercentId ? document.getElementById(config.progressPercentId) : null;
    var progressLabelEl = config.progressLabelId ? document.getElementById(config.progressLabelId) : null;
    var progressEnabled = !!(progressFill || progressWrap);
    var fakeProgressDurationMs = config.fakeProgressDurationMs || FAKE_PROGRESS_BASELINE_MS;

    if (!fileInput || !runBtn || !beforeSlot) {
      console.error("[VirtualTryOn] required elements missing", config);
      return { destroy: function () {} };
    }

    // Move the overlay to <body> so it escapes any ancestor's Shopify
    // section stacking context (a real bug hit in the original
    // implementation this is modeled on) — only touches an element you
    // already own and already styled yourself.
    if (overlay && overlay.parentElement !== document.body) {
      document.body.appendChild(overlay);
    }
    if (editorOverlay && editorOverlay.parentElement !== document.body) {
      document.body.appendChild(editorOverlay);
    }

    var uploadedFile = null;
    var resultUrl = null;
    var pollTimer = null;

    function setStatus(text) {
      if (statusEl) statusEl.textContent = text || "";
    }

    function setLoading(isLoading) {
      if (loadingTarget) loadingTarget.classList.toggle("loading", !!isLoading);
    }

    function setBeforeImage(src) {
      beforeSlot.innerHTML = '<img src="' + src + '" alt="Your photo">';
      bindLightbox(beforeSlot.querySelector("img"));
    }

    function setAfterImage(src) {
      if (!afterSlot) return;
      afterSlot.innerHTML = '<img src="' + src + '" alt="Try-on result">';
      bindLightbox(afterSlot.querySelector("img"));
    }

    function bindLightbox(imgEl) {
      if (!imgEl || !overlay || !overlayImg) return;
      imgEl.style.cursor = "pointer";
      imgEl.addEventListener("click", function () {
        overlayImg.src = imgEl.src;
        overlay.classList.remove("hidden");
        document.body.style.overflow = "hidden";
      });
    }

    function closeOverlay() {
      if (!overlay) return;
      overlay.classList.add("hidden");
      document.body.style.overflow = "";
      if (overlayImg) overlayImg.src = "";
    }

    function stopPolling() {
      if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    }

    function normalizeUrl(url) {
      if (!url) return url;
      // Protocol-relative URLs (e.g. "//cdn.shopify.com/...") are valid in
      // <img src> / CSS but get sent to the API as a literal string without
      // a scheme, which the API rejects. Match the reference implementation's
      // fix and force https: onto them before submitting — and the same
      // applies to the API's own result_url on the way back in: a
      // protocol-relative or bare-http URL assigned straight into
      // <img src> on an HTTPS storefront is silently blocked as mixed
      // content, which looks exactly like "the API succeeded but nothing
      // shows up".
      if (url.indexOf("//") === 0) return "https:" + url;
      if (url.indexOf("http://") === 0) return "https:" + url.slice(5);
      return url;
    }

    function resolveGarmentUrl() {
      if (typeof config.getGarmentUrl === "function") {
        var dynamic = config.getGarmentUrl();
        if (dynamic) return normalizeUrl(dynamic);
      }
      return normalizeUrl(config.garmentUrl) || null;
    }

    // ------------------------------------------------------------------
    // Photo editor: rotate / zoom / pan-to-crop
    // ------------------------------------------------------------------
    var editorImage = null;       // the HTMLImageElement being edited
    var editorBaseScale = 1;      // "cover" scale so the image fills the canvas
    var editorState = { zoom: 1, rotationDeg: 0, offsetX: 0, offsetY: 0 };
    var editorDrag = null;        // { startX, startY, startOffsetX, startOffsetY }
    var editorPendingFileName = "photo.jpg";
    var editorPendingFileType = "image/jpeg";

    function editorResetState() {
      editorState.zoom = 1;
      editorState.rotationDeg = 0;
      editorState.offsetX = 0;
      editorState.offsetY = 0;
      if (editorZoomRange) editorZoomRange.value = "100";
    }

    function openEditor(dataUrl, file) {
      if (!editorEnabled) {
        // No editor wired up — fall back to the original immediate-preview flow.
        commitPhoto(file, dataUrl);
        return;
      }

      editorPendingFileName = (file && file.name) || "photo.jpg";
      editorPendingFileType = (file && file.type) || "image/jpeg";

      var img = new Image();
      img.onload = function () {
        editorImage = img;
        editorResetState();
        // Make the overlay visible FIRST — while it's still display:none,
        // the canvas has no layout box, so clientWidth/clientHeight read 0
        // and sizeEditorCanvas() falls back to a hardcoded guess. Sizing
        // the canvas to that guess and then having CSS stretch it to the
        // real (differently-shaped) box is exactly what squishes the photo.
        editorOverlay.classList.add("active");
        document.body.style.overflow = "hidden";
        sizeEditorCanvas();
        drawEditor();
      };
      img.onerror = function () {
        console.error("[VirtualTryOn] could not load image for editing");
        commitPhoto(file, dataUrl);
      };
      img.src = dataUrl;
    }

    function sizeEditorCanvas() {
      if (!editorCanvas) return;
      var dpr = window.devicePixelRatio || 1;
      var displayW = editorCanvas.clientWidth || 320;
      var displayH = editorCanvas.clientHeight || 390;
      editorCanvas.width = Math.round(displayW * dpr);
      editorCanvas.height = Math.round(displayH * dpr);
      if (editorImage) {
        editorBaseScale = Math.max(
          editorCanvas.width / editorImage.naturalWidth,
          editorCanvas.height / editorImage.naturalHeight
        );
      }
    }

    function drawEditor() {
      if (!editorCtx || !editorImage || !editorCanvas) return;
      var w = editorCanvas.width;
      var h = editorCanvas.height;

      editorCtx.save();
      editorCtx.clearRect(0, 0, w, h);
      editorCtx.fillStyle = "#111111";
      editorCtx.fillRect(0, 0, w, h);

      var scale = editorBaseScale * editorState.zoom;
      var rad = (editorState.rotationDeg * Math.PI) / 180;

      editorCtx.translate(w / 2 + editorState.offsetX, h / 2 + editorState.offsetY);
      editorCtx.rotate(rad);
      editorCtx.scale(scale, scale);
      editorCtx.drawImage(
        editorImage,
        -editorImage.naturalWidth / 2,
        -editorImage.naturalHeight / 2,
        editorImage.naturalWidth,
        editorImage.naturalHeight
      );
      editorCtx.restore();
    }

    function clampOffsets() {
      // Loose clamp (ignores rotation for simplicity) so the shopper can't
      // drag the photo completely out of frame.
      var scale = editorBaseScale * editorState.zoom;
      var dispW = editorImage.naturalWidth * scale;
      var dispH = editorImage.naturalHeight * scale;
      var maxX = Math.max(0, (dispW - editorCanvas.width) / 2);
      var maxY = Math.max(0, (dispH - editorCanvas.height) / 2);
      editorState.offsetX = Math.max(-maxX, Math.min(maxX, editorState.offsetX));
      editorState.offsetY = Math.max(-maxY, Math.min(maxY, editorState.offsetY));
    }

    function onEditorPointerDown(e) {
      if (!editorImage) return;
      var point = e.touches ? e.touches[0] : e;
      editorDrag = {
        startX: point.clientX,
        startY: point.clientY,
        startOffsetX: editorState.offsetX,
        startOffsetY: editorState.offsetY
      };
      if (e.preventDefault) e.preventDefault();
    }

    function onEditorPointerMove(e) {
      if (!editorDrag) return;
      var point = e.touches ? e.touches[0] : e;
      editorState.offsetX = editorDrag.startOffsetX + (point.clientX - editorDrag.startX);
      editorState.offsetY = editorDrag.startOffsetY + (point.clientY - editorDrag.startY);
      clampOffsets();
      drawEditor();
      if (e.preventDefault) e.preventDefault();
    }

    function onEditorPointerUp() {
      editorDrag = null;
    }

    function onEditorWheel(e) {
      if (!editorImage) return;
      e.preventDefault();
      var delta = e.deltaY > 0 ? -0.08 : 0.08;
      setEditorZoom(editorState.zoom + delta);
    }

    function setEditorZoom(nextZoom) {
      editorState.zoom = Math.max(1, Math.min(3, nextZoom));
      if (editorZoomRange) editorZoomRange.value = String(Math.round(editorState.zoom * 100));
      clampOffsets();
      drawEditor();
    }

    function onEditorZoomInput() {
      if (!editorZoomRange) return;
      setEditorZoom(parseInt(editorZoomRange.value, 10) / 100);
    }

    function onEditorRotate(deltaDeg) {
      editorState.rotationDeg = (editorState.rotationDeg + deltaDeg) % 360;
      clampOffsets();
      drawEditor();
    }

    function onEditorReset() {
      editorResetState();
      clampOffsets();
      drawEditor();
    }

    function closeEditor() {
      if (!editorOverlay) return;
      editorOverlay.classList.remove("active");
      document.body.style.overflow = "";
      editorImage = null;
    }

    function onEditorCancel() {
      closeEditor();
      // Nothing was ever committed to uploadedFile/beforeSlot for this pick,
      // so clear the input to avoid a stale/half-picked file state.
      if (!uploadedFile) fileInput.value = "";
    }

    function onEditorApply() {
      if (!editorImage || !editorCanvas) {
        closeEditor();
        return;
      }
      // Render the exact framed crop to a dedicated export canvas, independent
      // of the on-screen canvas's device-pixel sizing.
      var exportSize = 1024;
      var exportCanvas = document.createElement("canvas");
      exportCanvas.width = exportSize;
      exportCanvas.height = Math.round(exportSize * (editorCanvas.height / editorCanvas.width));
      var exportCtx = exportCanvas.getContext("2d");

      var ratio = exportCanvas.width / editorCanvas.width;
      var scale = editorBaseScale * editorState.zoom * ratio;
      var rad = (editorState.rotationDeg * Math.PI) / 180;

      exportCtx.fillStyle = "#111111";
      exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
      exportCtx.save();
      exportCtx.translate(
        exportCanvas.width / 2 + editorState.offsetX * ratio,
        exportCanvas.height / 2 + editorState.offsetY * ratio
      );
      exportCtx.rotate(rad);
      exportCtx.scale(scale, scale);
      exportCtx.drawImage(
        editorImage,
        -editorImage.naturalWidth / 2,
        -editorImage.naturalHeight / 2,
        editorImage.naturalWidth,
        editorImage.naturalHeight
      );
      exportCtx.restore();

      exportCanvas.toBlob(
        function (blob) {
          if (!blob) {
            closeEditor();
            return;
          }
          var editedFile = new File([blob], editorPendingFileName, { type: editorPendingFileType });
          var previewUrl = exportCanvas.toDataURL(editorPendingFileType === "image/png" ? "image/png" : "image/jpeg", 0.92);
          closeEditor();
          commitPhoto(editedFile, previewUrl);
        },
        editorPendingFileType === "image/png" ? "image/png" : "image/jpeg",
        0.92
      );
    }

    function commitPhoto(file, previewSrc) {
      uploadedFile = file;
      resultUrl = null;
      setBeforeImage(previewSrc);
      runBtn.disabled = false;
      if (removeBtn) removeBtn.classList.remove("hidden");
      if (downloadBtn) downloadBtn.classList.add("hidden");
    }

    // ------------------------------------------------------------------
    // Fake progress bar
    // ------------------------------------------------------------------
    var progressRafId = null;
    var progressStartedAt = 0;

    function showProgressUI() {
      if (progressWrap) progressWrap.classList.remove("hidden");
      setProgressDisplay(0, FAKE_PROGRESS_STAGES[0].label);
    }

    function hideProgressUI() {
      if (progressWrap) progressWrap.classList.add("hidden");
    }

    // Paints the fill color directly onto the track's own background as a
    // hard-stop gradient, instead of animating a separate child element's
    // width. A width-animated child box depends on box-sizing, overflow,
    // and containing-block behaving exactly as expected, and in some
    // environments that silently fails to render even though the width is
    // correctly applied — this sidesteps all of that: it's one element,
    // one inline style, painted directly, nothing else can intercept it.
    function paintProgressTrack(percent) {
      if (!progressTrack) return;
      var pct = Math.max(0, Math.min(100, percent)) + "%";
      progressTrack.style.background =
        "linear-gradient(to right, #1a1a1a 0%, #D4AF37 " + pct + ", #e6e6e6 " + pct + ", #e6e6e6 100%)";
    }

    function setProgressDisplay(percent, label) {
      // percent === null means "leave the bar/percent exactly where they
      // are" — used on failure, so the fill freezes at whatever point it
      // reached instead of jumping anywhere.
      if (percent !== null) {
        var rounded = Math.round(percent);
        paintProgressTrack(rounded);
        if (progressPercentEl) progressPercentEl.textContent = rounded + "%";
      }
      if (label && progressLabelEl) progressLabelEl.textContent = label;
    }

    function stageLabelFor(fraction) {
      var label = FAKE_PROGRESS_STAGES[0].label;
      for (var i = 0; i < FAKE_PROGRESS_STAGES.length; i++) {
        if (fraction >= FAKE_PROGRESS_STAGES[i].at) label = FAKE_PROGRESS_STAGES[i].label;
      }
      return label;
    }

    function startFakeProgress() {
      if (!progressEnabled) return;
      showProgressUI();
      progressStartedAt = performance.now();

      function tick() {
        var elapsed = performance.now() - progressStartedAt;
        // Decelerating curve: approaches FAKE_PROGRESS_CAP but never reaches
        // it on its own — only the real API response pushes it to 100.
        var percent = FAKE_PROGRESS_CAP * (1 - Math.exp(-elapsed / (fakeProgressDurationMs / 3)));
        setProgressDisplay(percent, stageLabelFor(elapsed / fakeProgressDurationMs));
        if (progressTrack) {
          progressTrack.classList.toggle("tryon-progress--near-cap", percent > FAKE_PROGRESS_CAP - 4);
        }
        progressRafId = window.requestAnimationFrame(tick);
      }
      progressRafId = window.requestAnimationFrame(tick);
    }

    function stopFakeProgress(success) {
      if (progressRafId) {
        window.cancelAnimationFrame(progressRafId);
        progressRafId = null;
      }
      if (!progressEnabled) return;
      if (success) {
        if (progressTrack) progressTrack.style.background = "#1a1a1a";
        setProgressDisplay(100, "Done!");
        window.setTimeout(hideProgressUI, 450);
      } else {
        // A fast network/CORS failure can reject the fetch in the same
        // frame the bar appeared in — hiding immediately made it invisible.
        // Hold it, visibly marked as failed, for a beat before hiding.
        if (progressTrack) progressTrack.style.background = "#c0392b";
        setProgressDisplay(null, "Try-on failed");
        window.setTimeout(hideProgressUI, 1400);
      }
    }

    // ------------------------------------------------------------------
    // Upload / submit / poll
    // ------------------------------------------------------------------
    function onFileChange() {
      var file = fileInput.files && fileInput.files[0];
      if (!file) return;

      var reader = new FileReader();
      reader.onload = function (e) {
        openEditor(e.target.result, file);
      };
      reader.readAsDataURL(file);
    }

    function onRunClick() {
      if (!uploadedFile || runBtn.disabled) return;
      runTryOn(uploadedFile);
    }

    function onRemoveClick(e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      resetState();
    }

    function onDownloadClick() {
      if (!resultUrl) return;

      fetch(resultUrl)
        .then(function (res) { return res.blob(); })
        .then(function (blob) {
          var blobUrl = URL.createObjectURL(blob);
          var a = document.createElement("a");
          a.href = blobUrl;
          a.download = "virtual-tryon-" + Date.now() + ".png";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(blobUrl);
        })
        .catch(function (err) {
          console.error("[VirtualTryOn] download failed", err);
          window.open(resultUrl, "_blank");
        });
    }

    function resetState() {
      uploadedFile = null;
      resultUrl = null;
      fileInput.value = "";
      beforeSlot.innerHTML = "";
      if (afterSlot) afterSlot.innerHTML = "";
      setStatus("");
      setLoading(false);
      stopFakeProgress(false);
      runBtn.disabled = true;
      if (removeBtn) removeBtn.classList.add("hidden");
      if (downloadBtn) downloadBtn.classList.add("hidden");
      if (typeof config.onReset === "function") config.onReset();
    }

    function runTryOn(file) {
      var garmentUrl = resolveGarmentUrl();

      if (!garmentUrl || !config.productId) {
        setStatus("Try-on is unavailable for this product.");
        runBtn.disabled = false;
        return;
      }

      setStatus(progressEnabled ? "" : "Processing…");
      startFakeProgress();
      setLoading(true);
      runBtn.disabled = true;
      if (removeBtn) removeBtn.classList.add("hidden");
      if (downloadBtn) downloadBtn.classList.add("hidden");

      var formData = new FormData();
      formData.append("person_image", file);
      formData.append("garment_url", garmentUrl);
      formData.append("product_id", config.productId);
      formData.append("use_full_look", "true");

      fetch(API_BASE, { method: "POST", body: formData })
        .then(function (res) {
          if (!res.ok) {
            return res.text().then(function (bodyText) {
              console.error("[VirtualTryOn] submit failed body:", bodyText);
              throw new Error("Try-on request failed: " + res.status + " " + res.statusText);
            });
          }
          return res.json();
        })
        .then(function (data) {
          if (!data.request_id) throw new Error("No request_id returned");
          pollResult(data.request_id);
        })
        .catch(function (err) {
          console.error("[VirtualTryOn] submit error:", err);
          fail("Try-on failed: " + err.message);
        });
    }

    function pollResult(requestId) {
      var attempts = 0;
      stopPolling();

      pollTimer = setInterval(function () {
        attempts++;
        if (attempts > MAX_POLLS) {
          stopPolling();
          fail("Try-on is taking longer than expected. Please try again.");
          return;
        }

        fetch(API_BASE + requestId)
          .then(function (res) { return res.json(); })
          .then(function (data) {
            if (data.status === "completed") {
              stopPolling();
              var rawResultUrl = data.result_url || data.resultUrl || data.output_url || data.outputUrl
                || (data.result && (data.result.url || data.result.result_url))
                || (data.data && (data.data.result_url || data.data.resultUrl));
              succeed(normalizeUrl(rawResultUrl));
            } else if (data.status === "failed") {
              stopPolling();
              fail("Try-on failed" + (data.error || data.message ? (": " + (data.error || data.message)) : "."));
            }
          })
          .catch(function (err) {
            console.error("[VirtualTryOn] poll error:", err);
            stopPolling();
            fail("Try-on failed while checking status: " + err.message);
          });
      }, POLL_INTERVAL_MS);
    }

    function succeed(url) {
      if (!url) {
        fail("Try-on completed but no result image was returned.");
        return;
      }
      resultUrl = url;
      stopFakeProgress(true);
      setAfterImage(url);
      setStatus(progressEnabled ? "" : "Done! Here's your try-on result.");
      setLoading(false);
      runBtn.disabled = false;
      if (removeBtn) removeBtn.classList.remove("hidden");
      if (downloadBtn) downloadBtn.classList.remove("hidden");
      if (typeof config.onSuccess === "function") config.onSuccess(url);
    }

    function fail(message) {
      stopFakeProgress(false);
      setStatus(message || "Try-on failed. Please try again.");
      setLoading(false);
      runBtn.disabled = false;
      if (removeBtn) removeBtn.classList.remove("hidden");
    }

    if (uploadBtn) uploadBtn.addEventListener("click", function () { fileInput.click(); });
    fileInput.addEventListener("change", onFileChange);
    runBtn.addEventListener("click", onRunClick);
    if (removeBtn) removeBtn.addEventListener("click", onRemoveClick);
    if (downloadBtn) downloadBtn.addEventListener("click", onDownloadClick);
    if (overlayClose) overlayClose.addEventListener("click", closeOverlay);
    if (overlay) {
      overlay.addEventListener("click", function (e) {
        if (e.target === overlay) closeOverlay();
      });
    }

    if (editorEnabled) {
      editorCanvas.addEventListener("mousedown", onEditorPointerDown);
      editorCanvas.addEventListener("mousemove", onEditorPointerMove);
      window.addEventListener("mouseup", onEditorPointerUp);
      editorCanvas.addEventListener("touchstart", onEditorPointerDown, { passive: false });
      editorCanvas.addEventListener("touchmove", onEditorPointerMove, { passive: false });
      editorCanvas.addEventListener("touchend", onEditorPointerUp);
      editorCanvas.addEventListener("wheel", onEditorWheel, { passive: false });
      if (editorZoomRange) editorZoomRange.addEventListener("input", onEditorZoomInput);
      if (editorRotateLeft) editorRotateLeft.addEventListener("click", function () { onEditorRotate(-90); });
      if (editorRotateRight) editorRotateRight.addEventListener("click", function () { onEditorRotate(90); });
      if (editorResetBtn) editorResetBtn.addEventListener("click", onEditorReset);
      if (editorApplyBtn) editorApplyBtn.addEventListener("click", onEditorApply);
      if (editorCancelBtn) editorCancelBtn.addEventListener("click", onEditorCancel);
      window.addEventListener("resize", function () {
        if (editorOverlay && editorOverlay.classList.contains("active")) {
          sizeEditorCanvas();
          drawEditor();
        }
      });
    }

    var escHandler = function (e) {
      if (e.key !== "Escape") return;
      if (editorOverlay && editorOverlay.classList.contains("active")) {
        onEditorCancel();
        return;
      }
      closeOverlay();
    };
    document.addEventListener("keydown", escHandler);

    return {
      // Stops any in-flight polling without tearing down event listeners —
      // use this when a modal/section is closed mid-request but may be
      // reopened later (e.g. a PDP try-on modal).
      stopPolling: stopPolling,
      destroy: function () {
        stopPolling();
        stopFakeProgress(false);
        fileInput.removeEventListener("change", onFileChange);
        runBtn.removeEventListener("click", onRunClick);
        if (removeBtn) removeBtn.removeEventListener("click", onRemoveClick);
        if (downloadBtn) downloadBtn.removeEventListener("click", onDownloadClick);
        if (overlayClose) overlayClose.removeEventListener("click", closeOverlay);
        document.removeEventListener("keydown", escHandler);
      }
    };
  }

  window.VirtualTryOn = { init: init };
})(window);
